import { IVariantType } from '~/modules/campaign/types';

export type MetricKey = 'conversionRateMetric' | 'rpvMetric' | 'aovMetric' | 'totalRevenueMetric';
export type CampaignPriority = 'REVENUE' | 'CONVERSION' | 'PROFIT';

export enum OutcomeStatus {
  NEITHER_WINS = 'NEITHER_WINS',
  NEITHER_WINS_CONTROL_OUTPERFORMS = 'NEITHER_WINS_CONTROL_OUTPERFORMS',
  NEITHER_WINS_VARIANT_OUTPERFORMS = 'NEITHER_WINS_VARIANT_OUTPERFORMS',
  NEITHER_WINS_VARIANT_OUTPERFORMS_AND_EQUAL = 'NEITHER_WINS_VARIANT_OUTPERFORMS_AND_EQUAL',
  NEITHER_WINS_BOTH_OUTPERFORM = 'NEITHER_WINS_BOTH_OUTPERFORM',
  NEITHER_WINS_CONTROL_OUTPERFORM_BUT_FALLS_BEHIND = 'NEITHER_WINS_CONTROL_OUTPERFORM_BUT_FALLS_BEHIND',
  NEITHER_WINS_VARIANT_OUTPERFORM_BUT_FALLS_BEHIND = 'NEITHER_WINS_VARIANT_OUTPERFORM_BUT_FALLS_BEHIND',
  NEITHER_WINS_CONTROL_OUTPERFORMS_AND_EQUAL = 'NEITHER_WINS_CONTROL_OUTPERFORMS_AND_EQUAL',
  NEITHER_WINS_ALL_EQUAL_SECONDARY = 'NEITHER_WINS_ALL_EQUAL_SECONDARY',
  CONTROL_WINS = 'CONTROL_WINS',
  CONTROL_WINS_BUT_EQUAL = 'CONTROL_WINS_BUT_EQUAL',
  CONTROL_WINS_ALL_EQUAL_SECONDARY = 'CONTROL_WINS_ALL_EQUAL_SECONDARY',
  CONTROL_WINS_VARIANT_OUTPERFORMS = 'CONTROL_WINS_VARIANT_OUTPERFORMS',
  CONTROL_WINS_VARIANT_OUTPERFORMS_AND_EQUAL = 'CONTROL_WINS_VARIANT_OUTPERFORMS_AND_EQUAL',
  VARIANT_WINS = 'VARIANT_WINS',
  VARIANT_WINS_BUT_EQUAL = 'VARIANT_WINS_BUT_EQUAL',
  VARIANT_WINS_ALL_EQUAL_SECONDARY = 'VARIANT_WINS_ALL_EQUAL_SECONDARY',
  VARIANT_WINS_CONTROL_OUTPERFORMS = 'VARIANT_WINS_CONTROL_OUTPERFORMS',
  VARIANT_WINS_CONTROL_OUTPERFORMS_AND_EQUAL = 'VARIANT_WINS_CONTROL_OUTPERFORMS_AND_EQUAL',
  VARIANT_FALLS_BEHIND = 'VARIANT_FALLS_BEHIND',
  CONTROL_FALLS_BEHIND = 'CONTROL_FALLS_BEHIND',
  ALL_EQUAL = 'ALL_EQUAL',
  ERROR = 'ERROR',
  NO_DATA = 'NO_DATA',
  INSUFFICIENT_DATA = 'INSUFFICIENT_DATA',
  NO_SIGNIFICANT_WIN = 'NO_SIGNIFICANT_WIN',
  ERROR_CONTRADICTORY_CONDITION = 'ERROR_CONTRADICTORY_CONDITION',
}

export const NEITHER_WINS_DATA = [
  OutcomeStatus.ALL_EQUAL,
  OutcomeStatus.ERROR,
  OutcomeStatus.NO_DATA,
  OutcomeStatus.INSUFFICIENT_DATA,
  OutcomeStatus.NO_SIGNIFICANT_WIN,
  OutcomeStatus.ERROR_CONTRADICTORY_CONDITION,
  //
  OutcomeStatus.NEITHER_WINS,
  OutcomeStatus.NEITHER_WINS_CONTROL_OUTPERFORMS,
  OutcomeStatus.NEITHER_WINS_VARIANT_OUTPERFORMS,
  OutcomeStatus.NEITHER_WINS_VARIANT_OUTPERFORMS_AND_EQUAL,
  OutcomeStatus.NEITHER_WINS_BOTH_OUTPERFORM,
  OutcomeStatus.NEITHER_WINS_CONTROL_OUTPERFORM_BUT_FALLS_BEHIND,
  OutcomeStatus.NEITHER_WINS_VARIANT_OUTPERFORM_BUT_FALLS_BEHIND,
  OutcomeStatus.NEITHER_WINS_CONTROL_OUTPERFORMS_AND_EQUAL,
  OutcomeStatus.NEITHER_WINS_ALL_EQUAL_SECONDARY,
];

export const CONTROL_WINS = [
  OutcomeStatus.CONTROL_WINS,
  OutcomeStatus.CONTROL_WINS_BUT_EQUAL,
  OutcomeStatus.CONTROL_WINS_ALL_EQUAL_SECONDARY,
  OutcomeStatus.CONTROL_WINS_VARIANT_OUTPERFORMS,
  OutcomeStatus.CONTROL_WINS_VARIANT_OUTPERFORMS_AND_EQUAL,
];

export const VARIANT_WINS = [
  OutcomeStatus.VARIANT_WINS,
  OutcomeStatus.VARIANT_WINS_BUT_EQUAL,
  OutcomeStatus.VARIANT_WINS_ALL_EQUAL_SECONDARY,
  OutcomeStatus.VARIANT_WINS_CONTROL_OUTPERFORMS,
  OutcomeStatus.VARIANT_WINS_CONTROL_OUTPERFORMS_AND_EQUAL,
];

export const CONFIG = {
  THRESHOLD: 0,
  CONVERSION_THRESHOLD: 95,
  METRICS_BY_GOAL: {
    REVENUE: ['totalRevenueMetric', 'conversionRateMetric', 'aovMetric'] as MetricKey[],
    CONVERSION: ['conversionRateMetric', 'aovMetric', 'rpvMetric'] as MetricKey[],
    PROFIT: ['aovMetric', 'conversionRateMetric', 'totalRevenueMetric'] as MetricKey[],
  } as Record<CampaignPriority, MetricKey[]>,
  VARIANT_TYPE_LABEL: {
    [IVariantType.VARIANT_A]: 'Control',
    [IVariantType.VARIANT_B]: 'Variant',
  } as Record<IVariantType, string>,
  METRIC_NAME: {
    conversionRateMetric: 'Conversion rate',
    rpvMetric: 'Revenue per visitor',
    aovMetric: 'Average order value',
    totalRevenueMetric: 'Revenue',
  } as Record<MetricKey, string>,
};

export function boldMetric(metric: string): string {
  const metricNames = Object.values(CONFIG.METRIC_NAME);
  return metricNames.includes(metric) ? `<strong>${metric}</strong>` : metric;
}

export function boldText(metric: string): string {
  if (!metric) return '';
  return `<strong>${metric}</strong>`;
}

export function highlightVariant(variantType: IVariantType, isGroup: boolean): string {
  const variantTypeLabel = CONFIG.VARIANT_TYPE_LABEL[variantType];
  const textVariantCls = `text-variant-${variantType === IVariantType.VARIANT_A ? 'a' : 'b'}`;
  return `<span class="Polaris-Text--root Polaris-Text--bold ${textVariantCls}">${variantTypeLabel}</span>${isGroup ? ' group' : ''}`;
}

export const TEXT_TEMPLATES = {
  neitherWins: (metricName: string): string => `Neither version wins on ${boldMetric(metricName)}`,
  controlWins: (metrics: string, isGroup: boolean): string =>
    `${highlightVariant(IVariantType.VARIANT_A, isGroup)} wins on ${metrics}.`,
  variantWins: (metrics: string, isGroup: boolean): string =>
    `${highlightVariant(IVariantType.VARIANT_B, isGroup)} wins on ${metrics}.`,
  neitherOutperforms: (): string => `Neither version outperforms the other`,
  keepTesting: (): string => `Keep the test running to gather more data.`,
  outperforms: (version: string, metrics: string): string => `${version} outperforms on ${metrics}`,
  butOutperforms: (version: string, metrics: string): string =>
    `But the ${version} outperforms on ${metrics}`,
  equalIn: (metrics: string): string => `Both versions are equal in ${metrics}.`,
  equalInAnd: (metrics: string): string => `& both are equal in ${metrics}.`,
  noData: (metrics: string): string => `Neither version outperforms the other in ${metrics}.`,
  insufficientData: (): string => `Insufficient data for a reliable comparison.`,
  noSignificantWin: (): string => `No significant difference between versions`,
  fallsBehind: (metricName: string): string => `but falls behind in ${boldMetric(metricName)}.`,
  error: (): string => `Error: Missing primary metric data.`,
  contradictory: (): string =>
    `Error: Data contradicts expected win condition for Conversion goal.`,
};
